import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './HoursApprovalDetail.css';
import RequesterName from './RequesterName';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const day = (v) => (v ? String(v).slice(0, 10) : '—');
const when = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '—');

/**
 * One ticket, opened from a list that is not an approval queue.
 *
 * The approvals page has had this for a while - open the job, read what is
 * actually on it, then decide - and it is the same gesture anywhere a list of
 * tickets is something you are working through rather than just counting. So
 * this is the third component on HoursApprovalDetail.css rather than a fourth
 * visual language: a ticket should look the same whichever list you reached it
 * from.
 *
 * What it does not have is a decision. Nothing here signs anything off; this
 * is the read-only sibling, for the pages where the answer is "chase it", not
 * "approve it".
 *
 * Three reads rather than one, because unlike the approval queues there is no
 * single endpoint that assembles a ticket - and all three soft-fail to empty
 * on the server, so a missing comment table shows as no comments rather than
 * as a dialogue that will not open.
 */
export default function TicketPeekDetail({ ticketId, ticketNumber, title, onClose, extra }) {
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const closer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c, h] = await Promise.all([
        axios.get(`${API_BASE}/tickets/${ticketId}`),
        axios.get(`${API_BASE}/tickets/${ticketId}/comments`).catch(() => ({ data: [] })),
        axios.get(`${API_BASE}/tickets/${ticketId}/history`).catch(() => ({ data: [] }))
      ]);
      setTicket(t.data || null);
      setComments(Array.isArray(c.data) ? c.data : []);
      setHistory(Array.isArray(h.data) ? h.data : []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not read this ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    closer.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const t = ticket;

  return (
    <div
      className="had-scrim"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="had" role="dialog" aria-modal="true"
           aria-label={`${ticketNumber || 'Ticket'}: ${title || ''}`}>
        <div className="had-top">
          <div>
            <span className="had-num">{ticketNumber || '—'}</span>
            <h2>{t?.title || title || 'Ticket'}</h2>
          </div>
          <button type="button" className="had-x" onClick={onClose} ref={closer}
                  aria-label="Close">&times;</button>
        </div>

        {loading && <p className="had-quiet">Reading the ticket&hellip;</p>}
        {error && <p className="had-error" role="alert">{error}</p>}

        {!loading && !error && (
          <>
            <dl className="had-facts">
              <div><dt>Raised by</dt><dd><RequesterName ticket={t} fallback="unknown" /></dd></div>
              <div><dt>Email</dt><dd>{t?.requester_email || '—'}</dd></div>
              <div><dt>Status</dt><dd>{t?.status_name || t?.status || '—'}</dd></div>
              <div><dt>Priority</dt><dd>{t?.priority_name || t?.priority || '—'}</dd></div>
              <div><dt>Category</dt><dd>{t?.category_name || '—'}</dd></div>
              <div><dt>Department</dt><dd>{t?.department_name || '—'}</dd></div>
              <div><dt>Opened</dt><dd>{day(t?.created_at)}</dd></div>
              {t?.resolved_at && (
                <div><dt>Resolved</dt><dd>{day(t.resolved_at)}</dd></div>
              )}
              {t?.contractor_name && (
                <div><dt>Contractor</dt><dd>{t.contractor_name}</dd></div>
              )}
              {/* Whatever the list this was opened from knows that the ticket
                  itself does not - the SLA clocks, for instance. */}
              {(extra || []).map((e) => (
                <div key={e.label}><dt>{e.label}</dt><dd>{e.value}</dd></div>
              ))}
            </dl>

            <section className="had-sec">
              <h3>What was asked for</h3>
              {t?.description
                ? <p className="had-desc">{t.description}</p>
                : <p className="had-quiet">
                    Nothing was written in the description.
                  </p>}
            </section>

            <section className="had-sec">
              <h3>Comments<span className="had-count">{comments.length}</span></h3>
              {comments.length === 0
                ? <p className="had-quiet">Nothing has been written on this ticket.</p>
                : (
                  <ul className="had-feed">
                    {comments.map((c) => (
                      <li key={c.id}>
                        <span className="had-who">
                          {c.author_contractor || c.author_name || c.by_contractor
                            || c.by_user || 'unknown'}
                        </span>
                        <span className="had-at">{when(c.created_at)}</span>
                        <p>{c.comment}</p>
                      </li>
                    ))}
                  </ul>
                )}
            </section>

            <section className="had-sec">
              <h3>History<span className="had-count">{history.length}</span></h3>
              {history.length === 0
                ? <p className="had-quiet">No recorded changes.</p>
                : (
                  <ul className="had-feed is-tight">
                    {history.map((h) => (
                      <li key={h.id}>
                        <span className="had-who">{h.actor_name || 'system'}</span>
                        <span className="had-at">{when(h.created_at)}</span>
                        <p>{h.description || 'noted'}</p>
                      </li>
                    ))}
                  </ul>
                )}
            </section>

            <div className="had-actions had-decide">
              <button type="button" className="had-btn is-quiet" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
